import MultiRouteTour from "multiroute-tour-navigator";
import {
  MultiRouteTourProps,
  MultiRouteTourRefProps,
} from "multiroute-tour-navigator/lib/MultiRouteTour";
import {
  Align,
  ClientBoundingRect,
  HelperProps,
  Position,
  OverlayProps,
  Step,
} from "tour-navigator/lib/TourNavigator/types";
import TourHelper from "./TourHelper";
import TourHelperTooltip from "./TourHelperTooltip";
import {
  CSSProperties,
  forwardRef,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import Overlay from "./Overlay";
import QuestWrapper from "../QuestWrapper";
import config from "../../config";

import { claimCampaign, getExternalLogin, getQuestData } from "./walkthroughApi";
import { useGeneral } from "../../general";

type StyleConfig = {
  Form?: CSSProperties;
  Background?: CSSProperties;
  Heading?: CSSProperties;
  Description?: CSSProperties;
  Footer?: CSSProperties;
  FirstButton?: CSSProperties;
  LastButton?: CSSProperties;
  Image?: CSSProperties;
  Overlay?: CSSProperties;
  Arrow?: CSSProperties;
  Highlighter?: CSSProperties;
};

export interface BrandTheme {
  accentColor?: string;
  background?: string;
  borderRadius?: string;
  primaryButton?: string;
  secondaryButton?: string;
  contentColor?: string;
  fontFamily?: string;
  logo?: string;
  primaryColor?: string;
  secondaryColor?: string;
  tertiaryColor?: string;
  titleColor?: string;
}

export interface QuestThemeData {
  accentColor: string;
  theme: string;
  borderRadius: string;
  buttonColor: string;
  images: string[];
}

interface WalkThroughProps extends Omit<MultiRouteTourProps, 'steps'> {
  tooltip?: boolean;
  hideArrow?: boolean;
  onComplete?: () => void;
  styleConfig?: StyleConfig;
  firstButtonText?: string | ((currentStepIndex: number) => string);
  lastButtonText?: string | ((currentStepIndex: number) => string);
  finalButtonText?: string | ((currentStepIndex: number) => string); 
  handleFirstButtonClick?: (props: HelperProps) => void;
  handleLastButtonClick?: (props: HelperProps) => void;
  highlightStyle?: CSSProperties;
  highlightOpacity?: number;
  highlightStroke?: string;
  highlightStrokeWidth?: number;
  highlightStrokeDasharray?: number;
  isOnline?: boolean;
  variation?: string;
  campaignId?: string;
  userId?: string;
  token?: string;
  uniqueEmailId?: string;
  uniqueUserId?: string;
  steps?: Step[];
  footerText?:string;
  footerLink?:string;
  questId?: string;
  brandTheme?: BrandTheme;
  questThemeData?: QuestThemeData;
  showFooter?: boolean;
  entityAuthenticationToken?: string;
}

const Walkthrough = forwardRef<MultiRouteTourRefProps, WalkThroughProps>(
  (
    {
      tooltip,
      onComplete,
      hideArrow,
      styleConfig,
      firstButtonText,
      lastButtonText,
      handleFirstButtonClick,
      handleLastButtonClick,
      maskRadius = 5,
      maskOpacity = 1,
      maskStyle,
      overlayFill,
      overlayOpacity,
      maskStyleDuringScroll,
      highlightStyle,
      highlightOpacity,
      highlightStroke,
      highlightStrokeWidth,
      highlightStrokeDasharray,
      steps,
      isOnline = false,
      variation = "DEFAULT",
      campaignId,
      userId,
      token,
      uniqueEmailId,
      uniqueUserId,
      finalButtonText,
      footerText,
      footerLink,
      questId,
      brandTheme,
      questThemeData,
      entityAuthenticationToken,
      showFooter = true,
      ...props
    },
    ref
  ) => {
    const { themeConfig, entityId, apiType, apiKey, user } = useContext(
      QuestWrapper.Context
    );
    const [showFooterApi,setShowFooterApi] = useState(isOnline ? true : false);
    const [loading, setLoading] = useState(Boolean(isOnline && (campaignId || questId)));
    const [campaign, setCampaign] = useState<any>(null);
    const [userID, setUserId] = useState(userId || "");
    const [Token, setToken] = useState(token || "");
    const [BrandTheme, setBrandTheme] = useState(brandTheme);
    const [QuestThemeData, setQuestThemeData] = useState(questThemeData);

    const steps_ = useMemo<Step[]>(() => {
      if(campaign) {
        return campaign.actions?.map((action: any) => ({
          selector: action?.metadata?.link,
          data: {
            title: action?.title,
            description: action?.description,
            image: action?.metadata?.imageUrl,
            actionId: action?.actionId,
          },
          position: action?.metadata?.position as Position,
          align: action?.metadata?.align as Align,
        }))
      }
      return steps || []
    }, [steps, campaign])

    const tooltip_ = tooltip == undefined ? campaign?.sdkConfig?.uiProps?.tooltip || false : tooltip

    const themeConfigCSS = {
      "--background": themeConfig?.backgroundColor,
      "--primaryColor": themeConfig?.primaryColor,
      "--borderColor": themeConfig?.borderColor,
      "--buttonColor": themeConfig?.buttonColor,
      "--fontFamily": themeConfig?.fontFamily,
      "--secondaryColor": themeConfig?.secondaryColor,
    } as CSSProperties;

    let BACKEND_URL = apiType == "STAGING" ? config.BACKEND_URL_STAGING : config.BACKEND_URL;

    const general = useGeneral('quest')

    async function getWalkthroughData() {
      let updatedUserId = userID;
      let updatedToken = Token;

      if (campaignId || questId) {
        if (uniqueEmailId || uniqueUserId) {
          const response = await getExternalLogin({
            uniqueEmailId,
            uniqueUserId,
            entityId,
            apiKey,
            token,
            userId,
            apiType,
            entityAuthenticationToken,
          });

          if (response?.userId) {
            updatedUserId = response.userId;
            updatedToken = response.token;
            setUserId(() => updatedUserId);
            setToken(() => updatedToken);
          }
        }

        const questResponse = await getQuestData({
          userId: updatedUserId,
          token: updatedToken,
          entityId,
          apiKey,
          campaignId: campaignId || questId || "",
          variation,
          BACKEND_URL,
        });
        if (questResponse?.success) {
          setCampaign(questResponse.data);

          if (questResponse?.data?.sdkConfig?.uiProps?.BrandTheme) {
            setBrandTheme(questResponse?.data?.sdkConfig?.uiProps?.BrandTheme.properties);
          }
          if (questResponse?.data?.sdkConfig?.uiProps?.questThemeData) {
            setQuestThemeData(questResponse?.data?.sdkConfig?.uiProps?.questThemeData);
          }

          if(questResponse?.data?.sdkConfig?.uiProps?.showFooter !== undefined){
            setShowFooterApi(questResponse?.data?.sdkConfig?.uiProps?.showFooter);
          }
        }
      }
    }

    useEffect(() => {
      if (isOnline && (campaignId || questId)) {
        general.trackEvent(
          'quest-walkthrough-loaded',
          (uniqueUserId || userId || user?.userId)!,
          { campaignId: campaignId || questId },
          true,
          !uniqueUserId,
          true
        )
        getWalkthroughData()
        .finally(() => setLoading(false));
      }
    }, [isOnline]);

    const handleComplete = async () => {
      onComplete?.();
      setCampaign(campaign && {...campaign, isClaimed: true})

      try {
        if(isOnline && (campaignId || questId) && Token && userID){
          const data = {
            actions: steps_.map(step => ({
              actionId: step.data?.actionId,
              answers: [true]
            })),
            campaignVariationId: campaign?.campaignVariationId
          }
          await claimCampaign({
            apiKey,
            BACKEND_URL,
            campaignId: campaignId || questId || "",
            entityId,
            token: Token,
            userId: userID,
            data
          })

          general.trackEvent(
            'quest-walkthrough-completion',
            (uniqueUserId || userId || user?.userId)!,
            { campaignId: campaignId || questId },
            true,
            !uniqueUserId
          )
        }
      }catch(err){
        console.error(err)
      }
    }


    const CustomOverlay = (overlayProps: ClientBoundingRect) => (
      <Overlay
        id={props.id || ""}
        maskOpacity={maskOpacity}
        maskRadius={maskRadius}
        maskStyle={maskStyleDuringScroll || maskStyle}
        overlayFill={overlayFill}
        overlayOpacity={overlayOpacity}
        highlightOpacity={highlightOpacity}
        highlightStroke={highlightStroke}
        highlightStrokeWidth={highlightStrokeWidth}
        highlightStrokeDasharray={highlightStrokeDasharray}
        style={styleConfig?.Overlay}
        {...overlayProps}
      />
    );

    const CustomTourHelper = (helperProps: HelperProps) => {
      const TourHelperComponent = (tooltip_) ? TourHelperTooltip : TourHelper;

      return (
        <TourHelperComponent
          {...helperProps}
          helperStyle={{
            ...themeConfigCSS,
            ...styleConfig?.Form,
            borderRadius: QuestThemeData?.borderRadius || BrandTheme?.borderRadius || "10px"
          }}
          hideArrow={hideArrow}
          arrowStyle={styleConfig?.Arrow}
          headerStyle={styleConfig?.Heading || { color: BrandTheme?.titleColor }}
          descriptionStyle={styleConfig?.Description || { color: BrandTheme?.contentColor }}
          helperBackgroundStyle={styleConfig?.Background}
          footerStyle={styleConfig?.Footer}
          footerText={footerText}
          footerLink={footerLink}
          firstButtonStyle={styleConfig?.FirstButton}
          lastButtonStyle={styleConfig?.LastButton || { background: BrandTheme?.primaryColor || QuestThemeData?.accentColor || themeConfig?.primaryColor, color: (BrandTheme?.primaryColor || QuestThemeData?.accentColor || themeConfig?.primaryColor) && "white" }}
          imgStyle={styleConfig?.Image}
          onComplete={handleComplete}
          finalButtonText={finalButtonText}
          firstButtonText={firstButtonText}
          lastButtonText={lastButtonText}
          handleFirstButtonClick={handleFirstButtonClick}
          handleLastButtonClick={handleLastButtonClick}
          showFooter={showFooterApi}
        />
      );
    };

    if(loading || campaign?.isClaimed) return null;

    return (
      <MultiRouteTour
        {...props}
        ref={ref}
        helper={CustomTourHelper}
        overlay={CustomOverlay}
        steps={steps_}
      />
    );
  }
);

export default Walkthrough;

export type WalkthroughHelperProps = HelperProps;
export type WalkthroughOverlayProps = OverlayProps;
export { Align, Position };
